import { Button } from '@heroui/react';
import { StatusChip } from '@/components/common/StatusChip';
import { formatBytes } from '@/lib/format';
import { useSessionState } from '@/hooks/useSessionState';
import { MailboxesTable } from '@/features/mailboxes/components/MailboxesTable';
import type { MailboxGroup } from '@/features/mailboxes/groupMailboxes';
import type { Mailbox } from '@/features/mailboxes/types';
import { ChevronDownIcon, ChevronRightIcon } from '@/icons';

const COLLAPSED_KEY = 'nh.mailboxes.collapsedDomains';

/**
 * All-domains view: one collapsible section per tenant domain. Groups start
 * expanded; the collapsed set is remembered for the browser session. While
 * searching, matching groups are always shown expanded.
 */
export function MailboxDomainGroups({ groups, searching, onEdit, onReset, onDelete, onOpenInbox, onShowDomain, onCreateIn }: {
  groups: MailboxGroup[];
  searching: boolean;
  onEdit: (m: Mailbox) => void; onReset: (m: Mailbox) => void; onDelete?: (m: Mailbox) => void;
  onOpenInbox?: (m: Mailbox) => void;
  /** Switch to the single-domain view (server-paginated) for this domain. */
  onShowDomain: (domainId: string) => void;
  /** Open the create dialog preselected to this domain; omitted when the user may not create. */
  onCreateIn?: (domainId: string) => void;
}) {
  const [collapsed, setCollapsed] = useSessionState<string[]>(COLLAPSED_KEY, []);
  const toggle = (id: string) => setCollapsed((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  return (
    <div className="divide-y divide-border">
      {groups.map(({ summary: s, rows }) => {
        const open = searching || !collapsed.includes(s.domainId);
        const panelId = `mailbox-group-${s.domainId}`;
        const headingId = `${panelId}-heading`;
        const partial = !searching && rows.length < s.mailboxCount;
        return (
          <section key={s.domainId} aria-labelledby={headingId}>
            <h3 id={headingId} className="m-0">
              <button type="button" aria-expanded={open} aria-controls={panelId} disabled={searching}
                onClick={() => toggle(s.domainId)}
                className="flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-surface-raised focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand disabled:cursor-default sm:flex-row sm:items-center sm:gap-4">
                <span className="flex min-w-0 flex-wrap items-center gap-2 sm:flex-nowrap">
                  {open ? <ChevronDownIcon className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />
                    : <ChevronRightIcon className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />}
                  <span className="break-all font-semibold text-fg sm:truncate sm:break-normal">{s.domain}</span>
                  {!s.mailEnabled && <StatusChip tone={s.status === 'removed' ? 'danger' : 'warning'}>
                    {s.status === 'removed' ? 'Domain removed' : s.status === 'suspended' ? 'Suspended' : 'Not ready for email'}
                  </StatusChip>}
                </span>
                <span className="flex flex-wrap gap-x-4 gap-y-0.5 pl-6 text-xs text-fg-muted sm:ml-auto sm:pl-0">
                  <span>{searching ? `${rows.length} of ${s.mailboxCount}` : s.mailboxCount} {s.mailboxCount === 1 ? 'mailbox' : 'mailboxes'}</span>
                  <span>{formatBytes(s.usedBytes)} used</span>
                  {s.quotaBytes > 0 && <span>{formatBytes(s.quotaBytes)} allocated</span>}
                </span>
              </button>
            </h3>
            <div id={panelId} hidden={!open}>
              {open && (rows.length > 0 ? (
                <>
                  <MailboxesTable mailboxes={rows} isLoading={false} sort="address" onEdit={onEdit} onReset={onReset} onDelete={onDelete} onOpenInbox={onOpenInbox} />
                  {partial && (
                    <div className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-2 text-xs text-fg-muted">
                      Showing {rows.length} of {s.mailboxCount}.
                      <Button size="sm" variant="light" color="primary" onPress={() => onShowDomain(s.domainId)}>View all in {s.domain}</Button>
                    </div>
                  )}
                </>
              ) : s.mailboxCount > 0 ? (
                <div className="flex flex-wrap items-center gap-3 px-4 py-4 text-sm text-fg-muted">
                  {s.mailboxCount} {s.mailboxCount === 1 ? 'mailbox is' : 'mailboxes are'} on this domain.
                  <Button size="sm" variant="light" color="primary" onPress={() => onShowDomain(s.domainId)}>View mailboxes</Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3 px-4 py-4 text-sm text-fg-muted">
                  No mailboxes on this domain yet.
                  {onCreateIn && s.mailEnabled && <Button size="sm" variant="flat" color="primary" onPress={() => onCreateIn(s.domainId)}>Create mailbox</Button>}
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
